import React, { useState, useMemo, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faSearch,
    faFilter,
    faChartLine,
    faCheckCircle,
    faClock,
    faTimesCircle,
    faUsers,
    faTrophy,
    faGamepad,
} from "@fortawesome/free-solid-svg-icons";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip,
    Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const MSMETambolaHome = () => {

    const growthTriggers = [
        // COLUMN 1: Registration & Compliance (1–10)
        { id: 1, title: "Udyam Registration completed", theme: "Registration & Compliance" },
        { id: 2, title: "GST Registration active", theme: "Registration & Compliance" },
        { id: 3, title: "PAN & TAN verified", theme: "Registration & Compliance" },
        { id: 4, title: "Professional Tax compliance", theme: "Registration & Compliance" },
        { id: 5, title: "Shop & Establishment License", theme: "Registration & Compliance" },
        { id: 6, title: "Trade License updated", theme: "Registration & Compliance" },
        { id: 7, title: "Digital Signature Certificate (DSC)", theme: "Registration & Compliance" },
        { id: 8, title: "MCA annual filing completed", theme: "Registration & Compliance" },
        { id: 9, title: "FSSAI / sector-specific license obtained", theme: "Registration & Compliance" },
        { id: 10, title: "Trademark filed or registered", theme: "Registration & Compliance" },

        // COLUMN 2: Banking & Credit Readiness (11–20)
        { id: 11, title: "Current Account optimized", theme: "Banking & Credit Readiness" },
        { id: 12, title: "Cash Credit / OD facility active", theme: "Banking & Credit Readiness" },
        { id: 13, title: "CGTMSE-backed loan applied", theme: "Banking & Credit Readiness" },
        { id: 14, title: "CIBIL score above 700", theme: "Banking & Credit Readiness" },
        { id: 15, title: "Banking transactions fully digital", theme: "Banking & Credit Readiness" },
        { id: 16, title: "UPI / QR payment enabled", theme: "Banking & Credit Readiness" },
        { id: 17, title: "MSME loan pre-approved", theme: "Banking & Credit Readiness" },
        { id: 18, title: "Working capital cycle calculated", theme: "Banking & Credit Readiness" },
        { id: 19, title: "Separate business & personal finance", theme: "Banking & Credit Readiness" },
        { id: 20, title: "Emergency fund covering 3 months expenses", theme: "Banking & Credit Readiness" },

        // COLUMN 3: Digital Presence (21–30)
        { id: 21, title: "Google Business Profile created", theme: "Digital Presence" },
        { id: 22, title: "4+ star Google rating maintained", theme: "Digital Presence" },
        { id: 23, title: "Business website live", theme: "Digital Presence" },
        { id: 24, title: "Domain-based email ID used", theme: "Digital Presence" },
        { id: 25, title: "WhatsApp Business activated", theme: "Digital Presence" },
        { id: 26, title: "Facebook / Instagram page active", theme: "Digital Presence" },
        { id: 27, title: "LinkedIn business page created", theme: "Digital Presence" },
        { id: 28, title: "Online catalogue available", theme: "Digital Presence" },
        { id: 29, title: "SEO basics implemented", theme: "Digital Presence" },
        { id: 30, title: "Monthly digital posting calendar", theme: "Digital Presence" },

        // COLUMN 4: Sales & Marketing (31–40)
        { id: 31, title: "Defined Ideal Customer Profile (ICP)", theme: "Sales & Marketing" },
        { id: 32, title: "Sales script documented", theme: "Sales & Marketing" },
        { id: 33, title: "Monthly sales target fixed", theme: "Sales & Marketing" },
        { id: 34, title: "Lead tracking system implemented", theme: "Sales & Marketing" },
        { id: 35, title: "CRM tool adopted", theme: "Sales & Marketing" },
        { id: 36, title: "Referral program launched", theme: "Sales & Marketing" },
        { id: 37, title: "Customer feedback system", theme: "Sales & Marketing" },
        { id: 38, title: "Repeat customer rate above 30%", theme: "Sales & Marketing" },
        { id: 39, title: "Email / WhatsApp marketing campaign run", theme: "Sales & Marketing" },
        { id: 40, title: "Distributor / channel partner onboarded", theme: "Sales & Marketing" },

        // COLUMN 5: Cost Optimization & Profitability (41–50)
        { id: 41, title: "Monthly P&L prepared", theme: "Cost Optimization & Profitability" },
        { id: 42, title: "Break-even point calculated", theme: "Cost Optimization & Profitability" },
        { id: 43, title: "Cost reduction audit done", theme: "Cost Optimization & Profitability" },
        { id: 44, title: "Vendor renegotiation completed", theme: "Cost Optimization & Profitability" },
        { id: 45, title: "Inventory turnover tracked", theme: "Cost Optimization & Profitability" },
        { id: 46, title: "Waste reduction process implemented", theme: "Cost Optimization & Profitability" },
        { id: 47, title: "Unit economics calculated", theme: "Cost Optimization & Profitability" },
        { id: 48, title: "Pricing strategy reviewed", theme: "Cost Optimization & Profitability" },
        { id: 49, title: "Profit margin improved by 5%+", theme: "Cost Optimization & Profitability" },
        { id: 50, title: "Cash flow forecast prepared", theme: "Cost Optimization & Profitability" },

        // COLUMN 6: Technology Adoption (51–60)
        { id: 51, title: "Accounting software adopted", theme: "Technology Adoption" },
        { id: 52, title: "Inventory software implemented", theme: "Technology Adoption" },
        { id: 53, title: "Payroll automation done", theme: "Technology Adoption" },
        { id: 54, title: "Cloud data backup active", theme: "Technology Adoption" },
        { id: 55, title: "Digital invoice system", theme: "Technology Adoption" },
        { id: 56, title: "QR-based payment tracking", theme: "Technology Adoption" },
        { id: 57, title: "AI tool used for business task", theme: "Technology Adoption" },
        { id: 58, title: "E-commerce listing done", theme: "Technology Adoption" },
        { id: 59, title: "Cybersecurity basic setup", theme: "Technology Adoption" },
        { id: 60, title: "Automation implemented in one process", theme: "Technology Adoption" },

        // COLUMN 7: HR & Workforce (61–70)
        { id: 61, title: "Employee contracts documented", theme: "HR & Workforce" },
        { id: 62, title: "Attendance tracking system", theme: "HR & Workforce" },
        { id: 63, title: "Salary structure formalized", theme: "HR & Workforce" },
        { id: 64, title: "Skill training conducted", theme: "HR & Workforce" },
        { id: 65, title: "Incentive structure defined", theme: "HR & Workforce" },
        { id: 66, title: "ESIC/EPFO compliance", theme: "HR & Workforce" },
        { id: 67, title: "Clear job roles defined", theme: "HR & Workforce" },
        { id: 68, title: "SOP manual created", theme: "HR & Workforce" },
        { id: 69, title: "Performance review system", theme: "HR & Workforce" },
        { id: 70, title: "Team meeting schedule standardized", theme: "HR & Workforce" },

        // COLUMN 8: Government Schemes & Market Access (71–80)
        { id: 71, title: "Registered on GeM portal", theme: "Government Schemes & Market Access" },
        { id: 72, title: "NSIC registration", theme: "Government Schemes & Market Access" },
        { id: 73, title: "Export Import Code (IEC) obtained", theme: "Government Schemes & Market Access" },
        { id: 74, title: "Applied for subsidy scheme", theme: "Government Schemes & Market Access" },
        { id: 75, title: "Participated in government tender", theme: "Government Schemes & Market Access" },
        { id: 76, title: "Cluster association membership", theme: "Government Schemes & Market Access" },
        { id: 77, title: "District Industry Center visit completed", theme: "Government Schemes & Market Access" },
        { id: 78, title: "MSME Samadhan portal awareness", theme: "Government Schemes & Market Access" },
        { id: 79, title: "Quality certification (ISO etc.)", theme: "Government Schemes & Market Access" },
        { id: 80, title: "ZED certification applied", theme: "Government Schemes & Market Access" },

        // COLUMN 9: Scale, Growth & Expansion (81–90)
        { id: 81, title: "Revenue crossed ₹25 lakh", theme: "Scale, Growth & Expansion" },
        { id: 82, title: "Revenue crossed ₹50 lakh", theme: "Scale, Growth & Expansion" },
        { id: 83, title: "Revenue crossed ₹1 crore", theme: "Scale, Growth & Expansion" },
        { id: 84, title: "New product launched", theme: "Scale, Growth & Expansion" },
        { id: 85, title: "New city expansion", theme: "Scale, Growth & Expansion" },
        { id: 86, title: "Export inquiry received", theme: "Scale, Growth & Expansion" },
        { id: 87, title: "Strategic partnership formed", theme: "Scale, Growth & Expansion" },
        { id: 88, title: "Angel / investor pitch made", theme: "Scale, Growth & Expansion" },
        { id: 89, title: "Business continuity plan documented", theme: "Scale, Growth & Expansion" },
        { id: 90, title: "3-year growth roadmap prepared", theme: "Scale, Growth & Expansion" },
    ];

    const themes = [
        "All",
        "Registration & Compliance",
        "Banking & Credit Readiness",
        "Digital Presence",
        "Sales & Marketing",
        "Cost Optimization & Profitability",
        "Technology Adoption",
        "HR & Workforce",
        "Government Schemes & Market Access",
        "Scale, Growth & Expansion",
    ];

    const winnerPatterns = [
        {
            pattern: "Early 5",
            name: "Rahul Sharma",
            place: "Mumbai",
            country: "India",
            id: "T5W-1082",
            time: "16 Feb 2026, 07:42 PM IST",
        },
        {
            pattern: "Top Line",
            name: "Anita Verma",
            place: "Delhi",
            country: "India",
            id: "T5W-2147",
            time: "16 Feb 2026, 07:45 PM IST",
        },
        {
            pattern: "Middle Line",
            name: "Suresh Iyer",
            place: "Chennai",
            country: "India",
            id: "T5W-3309",
            time: "16 Feb 2026, 07:49 PM IST",
        },
        {
            pattern: "Bottom Line",
            name: "",
            place: "",
            country: "",
            id: "",
            time: "",
        },
        {
            pattern: "Corners",
            name: "Mohammed Khan",
            place: "Hyderabad",
            country: "India",
            id: "T5W-5098",
            time: "16 Feb 2026, 07:58 PM IST",
        },
        {
            pattern: "Full House",
            name: "",
            place: "",
            country: "",
            id: "",
            time: "",
        },
    ];

    const [searchQuery, setSearchQuery] = useState("");
    const [selectedTheme, setSelectedTheme] = useState("All");
    const [selectedTrigger, setSelectedTrigger] = useState(null);
    const [triggerStatuses, setTriggerStatuses] = useState({});
    const [currentCall, setCurrentCall] = useState(null);
    const [calledNumbers, setCalledNumbers] = useState([]);
    const [showHelp, setShowHelp] = useState(false);

    const [ttsEnabled, setTtsEnabled] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [revealKey, setRevealKey] = useState(0);
    
    const [gameStarted, setGameStarted] = useState(false);

    const [musicOn, setMusicOn] = useState(false);
    const backgroundMusic = useRef(null);
    const [userTicket, setUserTicket] = useState([]);

    const [showWinners, setShowWinners] = useState(true);

    const [presentationMode, setPresentationMode] = useState(false);

    useEffect(() => {
        const initialStatuses = {};
        growthTriggers.forEach((trigger) => {
            const randomStatus = ["Not Started", "In Progress", "Completed"][
            Math.floor(Math.random() * 3)
            ];
            initialStatuses[trigger.id] = randomStatus;
        });
        setTriggerStatuses(initialStatuses);
        generateTicket();
    }, []);

    const toggleFullScreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen();
        } else {
            document.exitFullscreen();
        }
        setPresentationMode(prev => !prev);
    };

    const callNextTrigger = () => {
        if (!gameStarted) {
            setGameStarted(true);
        }        
        if (calledNumbers.length === growthTriggers.length) return;

        const remaining = growthTriggers.filter(
            (t) => !calledNumbers.includes(t.id)
        );

        if (remaining.length === 0) return;

        const random =
            remaining[Math.floor(Math.random() * remaining.length)];

        // 🥁 Drum roll for big numbers (80+)
        if (random.id >= 80) {
            const drum = new Audio(
            "https://cdn.pixabay.com/download/audio/2022/03/10/audio_2f0c3e8c75.mp3?filename=drum-roll-110587.mp3"
            );
            drum.play().catch(() => {});
        }

        setCurrentCall(random);
        setCalledNumbers((prev) => [...prev, random.id]);
        setRevealKey((prev) => prev + 1);
    };

    const resetGame = () => {
        setCurrentCall(null);
        setCalledNumbers([]);
        setGameStarted(false); 
    };

    const generateTicket = () => {
        // Empty 3x9 grid
        const ticket = Array.from({ length: 3 }, () =>
            Array(9).fill(null)
        );

        // Step 1: Create 9 column ranges
        const columnRanges = [
            [1, 9],
            [10, 19],
            [20, 29],
            [30, 39],
            [40, 49],
            [50, 59],
            [60, 69],
            [70, 79],
            [80, 90],
        ];

        // Step 2: Pick 15 unique numbers properly distributed
        let selected = [];

        columnRanges.forEach(([min, max], colIndex) => {
            const count = Math.floor(Math.random() * 3); // 0–2 numbers per column

            const numbers = Array.from(
            { length: max - min + 1 },
            (_, i) => min + i
            ).sort(() => Math.random() - 0.5);

            for (let i = 0; i < count; i++) {
            selected.push({ number: numbers[i], col: colIndex });
            }
        });

        // Ensure exactly 15 numbers
        while (selected.length < 15) {
            const colIndex = Math.floor(Math.random() * 9);
            const [min, max] = columnRanges[colIndex];

            const num =
            Math.floor(Math.random() * (max - min + 1)) + min;

            if (!selected.some(n => n.number === num)) {
            selected.push({ number: num, col: colIndex });
            }
        }

        // Step 3: Distribute exactly 5 numbers per row (SAFE method)
        const rowCounts = [0, 0, 0];

        selected
            .sort((a, b) => a.col - b.col)
            .forEach((item, index) => {
            const row = index % 3;

            if (rowCounts[row] < 5) {
                ticket[row][item.col] = item.number;
                rowCounts[row]++;
            } else {
                // fallback to any available row
                for (let r = 0; r < 3; r++) {
                if (rowCounts[r] < 5 && ticket[r][item.col] === null) {
                    ticket[r][item.col] = item.number;
                    rowCounts[r]++;
                    break;
                }
                }
            }
            });

        setUserTicket(ticket);
    };

    const filteredTriggers = useMemo(() => {
        return growthTriggers.filter((trigger) => {
            const matchesSearch = trigger.title
            .toLowerCase()
            .includes(searchQuery.toLowerCase());
            const matchesTheme =
            selectedTheme === "All" || trigger.theme === selectedTheme;
            return matchesSearch && matchesTheme;
        });
    }, [searchQuery, selectedTheme]);

    useEffect(() => {
        if (!ttsEnabled || !currentCall) return;

        const synth = window.speechSynthesis;
        synth.cancel();

        // 🔔 Bell sound before announcement
        const bell = new Audio("https://actions.google.com/sounds/v1/cartoon/wood_plank_flicks.ogg");
        bell.play();

        const speakWithDelay = () => {
            const voices = synth.getVoices();

            // Try to find Indian English female voice
            const preferredVoice =
            voices.find(v =>
                v.lang.includes("en-IN") && v.name.toLowerCase().includes("female")
            ) ||
            voices.find(v => v.lang.includes("en-IN")) ||
            voices.find(v => v.lang.includes("en"));

            const utterance = new SpeechSynthesisUtterance(
            `Attention please! Number ${currentCall.id}. ${currentCall.title}`
            );

            if (preferredVoice) {
            utterance.voice = preferredVoice;
            }

            // 🎭 Dramatic Tambola style
            utterance.rate = 0.85;
            utterance.pitch = 1.2;
            utterance.volume = 1;

            utterance.onstart = () => setIsSpeaking(true);
            utterance.onend = () => setIsSpeaking(false);

            synth.speak(utterance);
        };

        setTimeout(speakWithDelay, 600);

        return () => synth.cancel();
    }, [currentCall, ttsEnabled]);

    useEffect(() => {
        if (!backgroundMusic.current) {
            backgroundMusic.current = new Audio(
            "https://cdn.pixabay.com/download/audio/2022/03/15/audio_c8b6d6a7f4.mp3?filename=suspense-ambience-110274.mp3"
            );

            backgroundMusic.current.loop = true;
            backgroundMusic.current.volume = 0.2;
        }

        if (musicOn) {
            backgroundMusic.current
            .play()
            .catch(err => console.log("Audio play blocked:", err));
        } else {
            backgroundMusic.current.pause();
        }
    }, [musicOn]);

const statusCounts = useMemo(() => {
    const counts = { Completed: 0, "In Progress": 0, "Not Started": 0 };
        Object.values(triggerStatuses).forEach((status) => {
        if (counts[status] !== undefined) counts[status]++;
        });
        return counts;
}, [triggerStatuses]);

const chartOptions = {
    responsive: true,
    plugins: {
        legend: {
            labels: {
                color: "#ffffff",
            },
        },
        tooltip: {
            titleColor: "#ffffff",
            bodyColor: "#ffffff",
        },
    },
    scales: {
        x: {
            ticks: {
                color: "#ffffff",
            },
            grid: {
                color: "rgba(255,255,255,0.1)",
            },
        },
        y: {
            ticks: {
                color: "#ffffff",
            },
            grid: {
                color: "rgba(255,255,255,0.1)",
            },
        },
    },
};

const chartData = {
    labels: ["Completed", "In Progress", "Not Started"],
    datasets: [
        {
            label: "Trigger Status Distribution",
            data: [
            statusCounts.Completed,
            statusCounts["In Progress"],
            statusCounts["Not Started"],
            ],
            backgroundColor: ["#22c55e", "#facc15", "#ef4444"],
        },
    ],
};

const updateStatus = (id, status) => {
    setTriggerStatuses((prev) => ({
    ...prev,
    [id]: status,
    }));
};

return (
    <div className="min-h-screen bg-black p-4">
        <div className="max-w-7xl mx-auto space-y-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                    <h1 className="text-yellow-200 text-lg lg:text-xl font-bold flex items-center gap-2">
                    <FontAwesomeIcon icon={faGamepad} />
                    MSME90 – A Gamified Engine for MSMEs
                    </h1>
                    <p className="text-white text-xs lg:text-sm mt-1">
                    Tambola for MSME Owners
                    </p>
                </div>
                <div className="flex items-center gap-4">
                    <span className="text-white text-xs lg:text-sm flex items-center gap-2">
                    <FontAwesomeIcon icon={faUsers} />
                    Multiplayer Simulation Mode
                    </span>
                </div>
                <button
                    onClick={() => setShowHelp(true)}
                    className="w-20 bg-cyan-700 px-3 py-2 rounded-lg text-white text-xs lg:text-sm"
                    >
                    Help
                </button>
                <button
                    onClick={toggleFullScreen}
                    className="absolute bottom-3 right-3 w-20 text-sm px-2 py-1 rounded bg-black text-white"
                    >
                    🖥
                </button>
            </div>

            {showHelp && (
                <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4"> 
                    <div className="bg-gray-900 rounded-2xl p-6 max-w-lg w-full space-y-4"> 
                        <h2 className="text-white text-lg lg:text-xl font-bold"> How MSME Tambola Works </h2>
                        <p className="text-white text-xs lg:text-sm">1. Press "Call Next" to generate a Growth Trigger.</p>
                        <p className="text-white text-xs lg:text-sm">2. If your business has completed that action, mark it as Completed.</p>
                        <p className="text-white text-xs lg:text-sm"> 3. If not, commit to completing it within 30 days.</p>
                        <p className="text-white text-xs lg:text-sm">4. Track progress in dashboard and aim for Business Transformation.</p>
                        <p className="text-white text-xs lg:text-sm">This is Growth-as-a-Game.</p>
                        <button
                            onClick={() => setShowHelp(false)}
                            className="bg-red-600 px-4 py-2 rounded-lg text-white text-xs lg:text-sm w-full"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}

            {/* Tambola Caller Panel */}
            <div className="bg-yellow-500 rounded-xl p-6 text-center shadow-lg relative">
                <button
                onClick={() => setTtsEnabled(prev => !prev)}
                title={ttsEnabled ? "Disable Voice" : "Enable Voice"}
                className={`absolute top-3 right-3 text-sm px-2 py-1 w-24 rounded transition ${
                    ttsEnabled ? "bg-green-600 text-white" : "bg-black text-white"
                }`}
                >
                    {ttsEnabled ? (isSpeaking ? "🔊✨" : "🔊") : "🔇"}
                </button>  
                <button
                onClick={() => setMusicOn(prev => !prev)}
                className="absolute top-3 left-12 text-sm px-2 py-1 w-24 rounded bg-black text-white"
                >
                {musicOn ? "🎵" : "🔕"}
                </button>

                <h2 className="mt-4 text-black text-sm lg:text-lg font-bold">
                    Live Caller
                </h2>

                {currentCall ? (
                    <>
                    <p
                    key={revealKey}
                    className={`text-black text-4xl lg:text-6xl font-extrabold mt-4 transition-all duration-500 ${
                        isSpeaking ? "scale-110" : "scale-100"
                    } animate-bounce`}
                    >
                    {currentCall.id}
                    </p>            
                    <p className="text-black text-xs lg:text-sm mt-2">
                        {currentCall.title}
                    </p>
                    </>
                ) : (
                    <p className="text-black text-sm lg:text-base mt-4">
                    Press Call to Start Game
                    </p>
                )}

                <div className="flex justify-center gap-4 mt-6">
                    <button
                    onClick={callNextTrigger}
                    className="bg-black px-4 py-2 rounded-lg text-white text-xs lg:text-sm font-semibold"
                    >
                    Call Next
                    </button>

                    <button
                    onClick={resetGame}
                    className="bg-gray-800 px-4 py-2 rounded-lg text-white text-xs lg:text-sm"
                    >
                    Reset
                    </button>
                    {currentCall && (
                        <button
                        onClick={() => setRevealKey(prev => prev + 1)}
                        className="bg-gray-700 px-3 py-2 rounded-lg text-white text-xs"
                        >
                        🔁
                        </button>
                    )}            
                </div>
                <p className="text-black text-xs mt-4">
                    Called: {calledNumbers.length} / 90
                </p>
            </div>

            {/* Winner Announcements */}
            <div className="bg-gradient-to-r from-yellow-400 to-orange-500 p-6 rounded-xl mb-6">
                <h2 className="text-black text-sm lg:text-lg font-bold mb-4">
                    🎉 Live Winner Announcements
                </h2>
                <button
                onClick={() => setShowWinners(prev => !prev)}
                className="w-32 text-xs mb-4 bg-black text-white px-4 py-2 rounded-lg"
                >
                 {showWinners ? "Hide Winners" : "Show Winners"}
                </button>
                {showWinners && (
                    <div className="bg-gradient-to-r from-yellow-400 to-orange-500 p-6 rounded-xl mb-6">
                        <h2 className="text-black text-sm lg:text-lg font-bold mb-4">🎉 Live Winner Announcements</h2>

                        <div className="grid lg:grid-cols-3 gap-4">
                        {winnerPatterns.map((winner, index) => {
                            const noWinner = !winner.name;

                            return (
                            <div
                                key={index}
                                className={`
                                p-4 rounded-xl shadow-lg text-xs lg:text-sm
                                ${noWinner
                                    ? "bg-gray-200 text-gray-500"
                                    : "bg-white"}
                                `}
                            >
                                <div className="font-bold text-orange-600 mb-2">
                                🏆 {winner.pattern}
                                </div>

                                {noWinner ? (
                                <div className="italic">
                                    No Winner Yet
                                </div>
                                ) : (
                                <>
                                    <div><strong>Name:</strong> {winner.name}</div>
                                    <div><strong>Place:</strong> {winner.place}</div>
                                    <div><strong>Country:</strong> {winner.country}</div>
                                    <div><strong>Think5Whys ID:</strong> {winner.id}</div>
                                    <div className="text-gray-600 mt-2">
                                    <strong>Achieved:</strong> {winner.time}
                                    </div>
                                </>
                                )}
                            </div>
                            );
                        })}
                        </div>
                    </div>
                )}
            </div>

            {/* Tambola Master Board */}
            <div className="bg-gray-900 p-6 rounded-xl">
                <h2 className="text-white text-sm lg:text-lg font-semibold mb-4">Tambola Board (1–90)</h2>

                <div className="grid grid-cols-10 gap-2">
                    {Array.from({ length: 90 }, (_, i) => {
                        const number = i + 1;
                        const isCalled = calledNumbers.includes(number);
                        const isCompleted = triggerStatuses[number] === "Completed";

                        return (
                            <div
                            key={number}
                            className={`
                                text-center text-xs lg:text-sm p-2 rounded-lg font-bold
                                ${isCompleted ? "bg-green-600 text-white" :
                                isCalled ? "bg-yellow-500 text-black" :
                                "bg-gray-800 text-white"}
                            `}
                            >
                                {number}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* User Ticket */}
            <div className="bg-yellow-100 p-6 rounded-xl">
                <h2 className="text-black text-sm lg:text-lg font-semibold mb-4">Your Tambola Ticket</h2>

                <div className="grid grid-rows-3 gap-2">
                    {userTicket.map((row, rowIndex) => (
                    <div key={rowIndex} className="grid grid-cols-9 gap-2">
                        {row.map((num, colIndex) => {
                        const isMarked = calledNumbers.includes(num);
                        return (
                            <div
                            key={colIndex}
                            className={`
                                text-center p-2 rounded-lg font-bold text-sm min-h-[35px]
                                ${
                                num
                                    ? isMarked
                                    ? "bg-green-600 text-white"
                                    : "bg-white text-black"
                                    : "bg-gray-700"
                                }
                            `}
                            >
                            {num || ""}
                            </div>
                        );
                        })}
                    </div>
                    ))}
                </div>

                <button
                    onClick={generateTicket}
                    disabled={gameStarted}
                    className="w-40 mt-4 bg-black text-white px-4 py-2 rounded-lg text-xs"
                >
                    Generate New Ticket
                </button>
            </div>


            {/* Dashboard Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-gray-900 p-4 rounded-xl">
                    <h2 className="text-white text-sm lg:text-lg font-semibold flex items-center gap-2">
                    <FontAwesomeIcon icon={faChartLine} />Progress Overview</h2>
                    <div className="mt-4"> <Bar data={chartData} options={chartOptions} /> </div>
                </div>

                <div className="bg-gray-900 p-4 rounded-xl flex flex-col justify-center">
                    <h2 className="text-white text-sm lg:text-lg font-semibold flex items-center gap-2">
                    <FontAwesomeIcon icon={faTrophy} />Business Transformation Score</h2>
                    <p className="text-white text-xs lg:text-sm mt-4">Completed Triggers: {statusCounts.Completed}</p>
                    <p className="text-white text-xs lg:text-sm">In Progress: {statusCounts["In Progress"]}</p>
                    <p className="text-white text-xs lg:text-sm">Not Started: {statusCounts["Not Started"]}</p>
                </div>
            </div>

            {/* Search & Filter */}
            <div className="bg-gray-900 p-4 rounded-xl space-y-4">
                <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faSearch} className="text-white" />
                    <input
                    type="text"
                    placeholder="Search Growth Trigger..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full bg-black text-white text-xs lg:text-sm p-2 rounded border border-gray-700 focus:outline-none"
                    />
                </div>

                <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faFilter} className="text-white" />
                    <select
                    value={selectedTheme}
                    onChange={(e) => setSelectedTheme(e.target.value)}
                    className="h-12 bg-black text-white text-xs lg:text-sm p-2 rounded border border-gray-700 focus:outline-none"
                    >
                    {themes.map((theme) => (
                        <option key={theme} value={theme} className="text-white">
                        {theme}
                        </option>
                    ))}
                    </select>
                </div>
            </div>

            {/* Trigger Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredTriggers.map((trigger) => (
                    <div
                    key={trigger.id}
                    onClick={() => setSelectedTrigger(trigger)}
                    className="bg-gray-900 p-4 rounded-xl cursor-pointer hover:bg-gray-800 transition"
                    >
                    <h3 className="text-white text-sm lg:text-base font-semibold">
                        #{trigger.id} {trigger.title}
                    </h3>
                    <p className="text-white text-xs lg:text-sm mt-2">
                        Theme: {trigger.theme}
                    </p>
                    <p className="text-white text-xs lg:text-sm mt-1 flex items-center gap-2">
                        {triggerStatuses[trigger.id] === "Completed" && (
                        <FontAwesomeIcon icon={faCheckCircle} />
                        )}
                        {triggerStatuses[trigger.id] === "In Progress" && (
                        <FontAwesomeIcon icon={faClock} />
                        )}
                        {triggerStatuses[trigger.id] === "Not Started" && (
                        <FontAwesomeIcon icon={faTimesCircle} />
                        )}
                        {triggerStatuses[trigger.id]}
                    </p>
                    </div>
                ))}
            </div>

            {/* Detail View */}
            {selectedTrigger && (
                <div className="bg-gray-900 p-6 rounded-xl space-y-4">
                    <h2 className="text-white text-lg lg:text-xl font-bold">
                    Trigger Detail – #{selectedTrigger.id}
                    </h2>
                    <p className="text-white text-sm lg:text-base">
                    {selectedTrigger.title}
                    </p>
                    <p className="text-white text-xs lg:text-sm">
                    Domain: {selectedTrigger.theme}
                    </p>

                    <div className="flex flex-wrap gap-2 mt-4">
                    {["Not Started", "In Progress", "Completed"].map((status) => (
                        <button
                        key={status}
                        onClick={() => updateStatus(selectedTrigger.id, status)}
                        className="text-xs lg:text-sm w-24 lg:text-32 px-3 py-1 bg-cyan-800 border border-gray-700 rounded text-white"
                        >
                        {status}
                        </button>
                    ))}
                    </div>
                </div>
            )}
        </div>
    </div>
    );
};

export default MSMETambolaHome;